import { del, get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'
import { toSchemaFields } from '@/features/dataFusion/api'
import { parentFields } from '@/features/hierarchy/parents'

/**
 * Sensors — the devices read off one connection, and the tags read off each.
 *
 * ── A sensor is a DEVICE, and its signals are its tags ────────────────────
 * `Sensor` and `SensorTag` are two tables. The sensor carries identity,
 * placement, the connection it reads from and the extraction settings its tags
 * SHARE; each tag carries one signal — its own value type, waveform shape,
 * engineering unit, waveform metadata and whichever extraction keys differ from
 * the device's. `SensorCreate.tags` is `min_length=1`, so a device always has at
 * least one.
 *
 * This file used to model a sensor as one flat signal, with `value_type`,
 * `waveform_type_id`, `waveform_metadata` and `value_unit_id` on the sensor
 * itself. Those columns moved to `SensorTag`, and a flat payload therefore
 * created a device with no signals at all.
 *
 * ── A tag id is permanent ─────────────────────────────────────────────────
 * `_replace_tags` is a DELTA, not a blanket replace: a tag sent with its `id` is
 * edited in place, one sent with `id: null` is created, and one simply left out
 * is soft-deleted. The server's own comment says why — the id is a QuestDB and
 * Kafka partition key and is never reissued. So an edit form must round-trip
 * every id it was given, and dropping one is a deletion rather than an omission.
 *
 * ── The whole resource is live ────────────────────────────────────────────
 * `POST`, `GET` (collection and detail), `PUT` and `DELETE` are all served.
 * `PUT` arrived after this feature was written and is NARROWER than `POST` — see
 * `updateSensor` for what it will and will not accept, and why sending the extra
 * fields anyway would fail silently rather than loudly.
 *
 * ── The one paginated resource in this API ────────────────────────────────
 * Every other collection endpoint returns the whole set. `GET /sensors` takes
 * `page` and `page_size` (max 200) and answers with
 * `{items, page, page_size, total, total_pages}`. That is not a nicety here: a
 * connection can carry more than ten thousand sensors, each with an
 * `extraction_config` JSONB, so fetching the collection to slice it client-side —
 * which is what every other list screen in this app does — would pull megabytes to
 * show fifty rows.
 *
 * ── Request params are snake_case, deliberately ───────────────────────────
 * `features/alerts/api.js` sends camelCase `pageSize`, because the only thing
 * reading it is the fixture adapter. This endpoint is a real FastAPI signature
 * (`page_size: int = Query(default=50, ge=1, le=200)`) and a camelCase key would be
 * silently ignored — the server would answer page 1 of 50 and the footer would
 * disagree with the rows. So the wire names are written out as the server declares
 * them, and the conversion happens here rather than at the call site.
 */

/** Longest `name` the `sensors.name` column accepts. */
export const NAME_MAX_LENGTH = 150

/**
 * Longest `description` the column accepts, and it is REQUIRED — `Field(min_length=1,
 * max_length=500)` on both `SensorCreate` and `SensorUpdate`.
 *
 * Required with no server-side default, so there is nothing to fall back to: a
 * sensor cannot be created without one. Enforced in the form rather than left to
 * the API because `min_length` is a Pydantic constraint, which returns FastAPI's
 * own 422 shape rather than the field-keyed envelope — see `formErrors`.
 */
export const DESCRIPTION_MAX_LENGTH = 500

/**
 * The `ValueType` enum, in the order the backend declares it.
 *
 * Four members. Never translated as a wire value; the label beside it is — see
 * `dataFusion.sensors.valueType.*`.
 */
export const VALUE_TYPES = ['integer', 'float', 'enum', 'string']

/**
 * What a new sensor starts as.
 *
 * `float`, not the enum's first member. A plant sensor reads a measurement far
 * more often than a count, and the default should be the answer that is usually
 * right rather than the one that happens to be declared first.
 */
export const DEFAULT_VALUE_TYPE = 'float'

/**
 * The largest page the server will serve. `Query(..., le=200)` rejects anything
 * above it with a 422, so this is a hard ceiling rather than a preference.
 */
export const MAX_PAGE_SIZE = 200

/* ══ MASTER DATA ═══════════════════════════════════════════════════════ */

/**
 * Waveform types and value units — platform master data, not tenant records.
 *
 * Both tables are global, read-only and seeded (`WaveformType` and `ValueUnit`
 * both say so in as many words), with no create, update or delete route. The
 * same arrangement as the connector catalogue, and the same consequence: the
 * only thing this app does with them is read the list once and offer it.
 *
 * ── Both are required on a sensor, which makes them load-bearing ──────────
 * `waveform_type_id`, `waveform_metadata` and `value_unit_id` are all
 * non-nullable on `SensorCreate` AND `SensorUpdate`. So neither form can be
 * submitted before these two requests land, and neither has a defaultable value
 * to fall back on if they fail — which is why `SensorFormPage` gates on them
 * rather than degrading.
 *
 * ── A waveform type carries a schema, a value unit does not ───────────────
 * `metadata_schema` is the same `{"fields": [...]}` format as a connector's
 * `config_schema`, validated by the same server-side walk, so it is mapped with
 * the same field mapper and assembled with the same builder. It is what the
 * chosen shape needs beyond the raw signal — an engineering range and an alarm
 * band, plus an update interval for the shapes whose detection recipe is
 * phase-timing rather than threshold. Which is why the metadata fields are
 * generated from the SELECTED type and change when it changes.
 */
const toWaveformType = (row) => ({
  id: row.id,
  key: row.key,
  name: row.name,
  metadataFields: toSchemaFields(row.metadata_schema),
})

/**
 * `symbol` is separate from `name` and both are shown: the name disambiguates
 * ("Pounds per Square Inch"), the symbol is what appears beside a reading
 * ("psi"). It is legitimately empty for `count`, so nothing may assume it prints.
 */
const toValueUnit = (row) => ({
  id: row.id,
  key: row.key,
  name: row.name,
  symbol: row.symbol ?? '',
})

/**
 * @returns {Promise<{items: Array<object>}>}
 *
 * `waveform_types`, not `items`. Every collection in this API names itself; both
 * of these read the wrong key for long enough to be worth naming here, because
 * the symptom — an empty catalogue — renders as a form that cannot be submitted
 * rather than as an error.
 */
export async function fetchWaveformTypes(signal) {
  const data = await get(endpoints.sensors.waveformTypes, { signal })
  return { items: (data?.waveform_types ?? []).map(toWaveformType) }
}

/** @returns {Promise<{items: Array<object>}>} */
export async function fetchValueUnits(signal) {
  const data = await get(endpoints.sensors.valueUnits, { signal })
  return { items: (data?.value_units ?? []).map(toValueUnit) }
}

/**
 * The waveform type a stored id refers to, or null.
 *
 * Null is a state the form has to handle rather than an impossibility: the two
 * tables are seeded and a sensor holds a foreign key into them, so a database
 * seeded before a type existed — or one where the seed was re-run after a key
 * rename — can hold an id the catalogue no longer lists. Without this the edit
 * form would generate its metadata fields from an empty list, quietly drop every
 * stored `range`/`alarm` value, and write the loss back on the next save.
 */
export const waveformTypeById = (types, id) => (types ?? []).find((type) => type.id === id) ?? null

/**
 * One tag — one signal on a device.
 *
 * `name` and `description` are nullable on the wire and land as `''` here, so a
 * control binds to them without going uncontrolled. They are sent back as null
 * when blank; see `toTagPayload`.
 *
 * `extractionConfig` is the tag's OVERRIDES only, not its effective settings. The
 * server merges it over the device's before validating, so a key absent here
 * means "whatever the device says" rather than "nothing". Reading it as the whole
 * truth is what would make a signal panel look empty on a device that fills every
 * field.
 */
const toSensorTag = (row) => ({
  id: row.id,
  name: row.name ?? '',
  description: row.description ?? '',
  valueType: row.value_type,
  extractionConfig: row.extraction_config ?? {},
  waveformTypeId: row.sensor_waveform_type_id,
  waveformMetadata: row.waveform_metadata ?? {},
  valueUnitId: row.sensor_value_unit_id,
})

/**
 * Wire → app.
 *
 * `extractionConfig` here is the DEVICE's — the settings its tags share, and the
 * fallback the server merges each tag's own over. It is carried through as the
 * plain object the server stored, unmasked: nothing in a sensor is a credential,
 * unlike a connection's `auth.password`, so an edit form can round-trip it
 * without the blank-and-ask-again dance `ConnectionDialogs` needs.
 *
 * What is NOT here, because `SensorOut` does not carry it: the parent's name, the
 * connection's name, the asset class's name, and `created_at`. Every one of those
 * is a client-side join against a list the screen has already cached.
 */
const toSensor = (row) => ({
  id: row.id,
  name: row.name,
  description: row.description ?? '',
  plantId: row.plant_id,
  lineId: row.line_id,
  machineId: row.machine_id,
  assetClassId: row.asset_class_id,
  connectionId: row.sensor_connection_id,
  extractionConfig: row.extraction_config ?? {},
  tags: (row.tags ?? []).map(toSensorTag),
})

/**
 * One tag, app → wire.
 *
 * Every key is PRESENT even when null, which is this API's standing rule and not
 * a preference: `SensorTagCreate` declares `name: str | None` with no default, so
 * omitting the key is a 422 rather than a null. Blank text becomes null because
 * the column is nullable and `''` is not the same answer as "unnamed".
 *
 * `id` is added by `toTagPayload` only on update — `SensorTagCreate` has no such
 * field, and the schema sets no `model_config`, so a stray `id` on create would
 * be dropped silently rather than rejected.
 */
const toTagPayload = ({ tag, withId }) => ({
  ...(withId ? { id: tag.id ?? null } : {}),
  name: tag.name?.trim() || null,
  description: tag.description?.trim() || null,
  value_type: tag.valueType,
  extraction_config: tag.extractionConfig,
  sensor_waveform_type_id: tag.waveformTypeId,
  waveform_metadata: tag.waveformMetadata,
  sensor_value_unit_id: tag.valueUnitId,
})

/**
 * One page of sensors, by connection or by machine.
 *
 * ── Two callers, two ways in ──────────────────────────────────────────────
 * Data Fusion asks for one CONNECTION's devices, which is the table this was
 * written for. The logbook's sensor-tag picker asks for one MACHINE's, because
 * a window pinned to a note is pinned to a signal on the asset the note is
 * about — and which broker that signal happens to arrive through is not a
 * question the person writing the note can answer.
 *
 * Both are real query params on `list_sensors` and both are optional there, so
 * neither caller constrains the other. Sending neither returns the whole
 * account, which is why the hook refuses to run without one.
 *
 * ── `assetType`/`assetId` is the SUBTREE filter, and it is a third axis ──
 * `apply_asset_scope` matches the named asset or any descendant of it, where
 * `machine_id`/`line_id`/`plant_id` match that column exactly. So a line's own
 * sensors come back for `line_id`, and a line's sensors PLUS its machines' come
 * back for `asset_type=line`. The anomaly sensor-tag picker wants the second.
 *
 * ── The three hierarchy ids are EXACT, and that is the difference ───────
 * `machine_id`, `line_id` and `plant_id` each compare one column —
 * `Sensor.plant_id == plant_id` — so `plant_id` returns the sensors attached to
 * the plant ITSELF and not those on its lines and machines. That is the narrow
 * reading and it is the one the anomaly sensor picker wants, because it is the
 * same rule `verify_linked_sensor_tags` applies when the tag is linked.
 *
 * @param {{connectionId?: string, plantId?: string, lineId?: string, machineId?: string, assetType?: string, assetId?: string, page: number, pageSize: number}} params
 * @returns {Promise<{items: Array<object>, page: number, pageSize: number, total: number, totalPages: number}>}
 */
export async function fetchSensors(
  { connectionId, plantId, lineId, machineId, assetType, assetId, page, pageSize },
  signal,
) {
  const data = await get(endpoints.sensors.list, {
    signal,
    params: {
      // `sensor_connection_id` — the query param is named for the column, and an
      // unrecognised param is IGNORED by FastAPI rather than rejected, so the
      // wrong spelling returns every sensor on the account under one connection's
      // heading instead of erroring. The same trap applies to `machine_id`.
      ...(connectionId ? { sensor_connection_id: connectionId } : {}),
      ...(machineId ? { machine_id: machineId } : {}),
      ...(lineId ? { line_id: lineId } : {}),
      ...(plantId ? { plant_id: plantId } : {}),
      /* Both or neither: `apply_asset_scope` is a no-op unless it has the pair,
         so sending one alone silently widens the list to the whole account. */
      ...(assetType && assetId ? { asset_type: assetType, asset_id: assetId } : {}),
      page,
      page_size: pageSize,
    },
  })

  return {
    items: (data?.sensors ?? []).map(toSensor),
    // Echoed back by the server rather than assumed from what was asked for: a
    // clamped or defaulted page must move the footer, not be contradicted by it.
    page: data?.page ?? page,
    pageSize: data?.page_size ?? pageSize,
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 0,
  }
}

/** One sensor. */
export async function fetchSensor(id, signal) {
  return toSensor(await get(endpoints.sensors.detail(id), { signal }))
}

/**
 * Create.
 *
 * `parent` is `{tier, id}` and becomes exactly one of `plant_id`/`line_id`/
 * `machine_id` — see `parentFields`. All three keys are sent, two of them null.
 *
 * A rejected parent comes back as `NotFoundError("Plant"|"Line"|"Machine")` at 404,
 * NOT as a field error: the server verifies the parent belongs to the caller's
 * account before it validates anything else. A parent that is missing entirely is
 * caught by a Pydantic `model_validator`, which returns FastAPI's own 422 shape
 * rather than the field-keyed envelope — so the form checks that itself before
 * sending. See SensorForm.
 *
 * ── `asset_class_id` is the one optional reference here ───────────────────
 * `|| null` rather than omitted, and that distinction matters: the column is
 * nullable and the schema declares `uuid | None`, so null is a legitimate stored
 * value meaning "unclassified". A tag's waveform type and value unit are NOT
 * nullable, so a blank one is a form error rather than a null.
 *
 * ── `tags` is required and must hold at least one ─────────────────────────
 * `Field(min_length=1)`, and a Pydantic length constraint returns FastAPI's 422
 * shape rather than the field-keyed envelope — which names no control. So the
 * form guarantees a signal before it sends, the same way it guarantees a parent.
 *
 * @param {{
 *   name: string, description: string, parent: {tier: string, id: string},
 *   assetClassId: ?string, connectionId: string, extractionConfig: object,
 *   tags: Array<object>,
 * }} input
 */
export async function createSensor({
  name,
  description,
  parent,
  assetClassId,
  connectionId,
  extractionConfig,
  tags,
}) {
  const row = await post(endpoints.sensors.list, {
    name: name.trim(),
    description: description.trim(),
    ...parentFields(parent),
    asset_class_id: assetClassId || null,
    sensor_connection_id: connectionId,
    extraction_config: extractionConfig,
    tags: tags.map((tag) => toTagPayload({ tag, withId: false })),
  })
  return toSensor(row)
}

/**
 * Update.
 *
 * ── The payload is narrower than create, and that is the contract ─────────
 * `SensorUpdate` accepts `name`, `description`, `asset_class_id`,
 * `extraction_config` and `tags` — everything create takes EXCEPT the parent trio
 * and `sensor_connection_id`. So neither where a sensor attaches nor which broker
 * it reads from can change after onboarding, the same shape `ConnectionUpdate`
 * takes when it drops `plant_id` and `sensor_connector_type_id`.
 *
 * Sending the excluded keys anyway would be worse than useless: the schema sets
 * no `model_config`, so Pydantic IGNORES unknown keys rather than rejecting them.
 * A form that offered to re-attach a sensor would report success and change
 * nothing. So the fields are absent here and locked in the form — see
 * SensorForm's `parentLocked`.
 *
 * ── `tags` is a delta, and an omission is a DELETION ──────────────────────
 * `_replace_tags` keeps the ids it is given, creates the ones sent as null, and
 * soft-deletes everything else that was on the sensor. So this list has to be the
 * complete intended state and not a patch: a form that sent only the tag somebody
 * edited would silently remove the other nine. An id that does not belong to this
 * sensor is refused with a field error on `tags`.
 *
 * @param {{
 *   id: string, name: string, description: string, assetClassId: ?string,
 *   extractionConfig: object, tags: Array<object>,
 * }} input
 */
export async function updateSensor({
  id,
  name,
  description,
  assetClassId,
  extractionConfig,
  tags,
}) {
  const row = await put(endpoints.sensors.detail(id), {
    name: name.trim(),
    description: description.trim(),
    asset_class_id: assetClassId || null,
    extraction_config: extractionConfig,
    tags: tags.map((tag) => toTagPayload({ tag, withId: true })),
  })
  return toSensor(row)
}

/**
 * Soft delete. The row keeps its identity with `deleted_at` set and drops out of
 * the listing.
 *
 * Nothing downstream is notified: `delete_sensor` carries a standing TODO to
 * publish a config-update event so the tag leaves active ingestion, and there is
 * no event bus in the backend to publish it to. So the confirmation says the
 * sensor stops being listed, and does not claim it stops being read.
 */
export async function deleteSensor(id) {
  await del(endpoints.sensors.detail(id))
  return { id, deleted: true }
}
